import { css } from '@emotion/react'
import { memo, useCallback } from 'react'
import { openMenuDialog } from '../../../../../actions/menu'
import { goToNewsFromModel } from '../../../../../actions/nav'
import { openNewsChatNotificationsDrawer } from '../../../../../app-state/modals/state.drawer'
import { TransitionTypes } from '../../../../../utilities/transitions'
import ButtonSettings from '../../../../Buttons/ButtonSettings'
import { useUserSelector } from '../../../../Hooks/useUser'
import BadgeAvatar from '../../../../common/BadgeAvatar'
import { palette } from '../../../../styles/colors'
import ModelName from '../../../Grid/ModelName'

const formatAMPM = (timestamp) => {
    const date = new Date(timestamp)
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    const hoursY = hours % 12
    const hoursX = hoursY ? hoursY : 12 // the hour '0' should be '12'
    const minutesX = minutes < 10 ? '0' + minutes : minutes
    const strTime = hoursX + ':' + minutesX + ' ' + ampm
    return strTime
}

const avatarContainerStyles = css({
    alignItems: 'center',
    paddingBottom: 14,
    display: 'flex',
})
const nameContainerStyles = css({
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    display: 'flex',
    marginLeft: 12,
})
const textStyles = css({
    borderTop: `1px solid ${palette.drawerDivider}`,
    background: palette.background,
    borderRadius: 4,
    padding: 8,
})
const settingsStyles = css({
    textDecoration: 'underline',
    justifyContent: 'right',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: -8,
    marginRight: -8,
    fontWeight: 500,
    display: 'flex',
    fontSize: 12,
    '& > div': { marginRight: 8 },
})

type PMChatNewsHeaderProps = {
    timestamp?: number
    text?: string
    id: number
}

const PMChatNewsHeader = memo((props: PMChatNewsHeaderProps) => {
    const { timestamp, text, id } = props
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isOffline = useUserSelector(id, 'isOffline')
    const color = useUserSelector(id, 'color')
    const name = useUserSelector(id, 'name')
    const slug = useUserSelector(id, 'slug')

    const onOpenMenu = useCallback(() => {
        openMenuDialog(id, {
            message: true,
            transitionSource: TransitionTypes.ROOM,
        })
    }, [id])

    const onViewAllNews = useCallback(() => goToNewsFromModel(slug), [slug])

    return (
        <div css={{ width: '100%' }}>
            <div
                data-testid="News__AvatarContainer"
                onClick={onOpenMenu}
                css={avatarContainerStyles}
            >
                <BadgeAvatar
                    broadcasting={isBroadcasting}
                    offline={isOffline}
                    size={40}
                    id={id}
                />
                <div css={nameContainerStyles}>
                    <ModelName color={color} modelId={id} name={name} />
                    {!!timestamp && (
                        <div style={{ fontSize: 12 }}>
                            {`at ${formatAMPM(timestamp)}`}
                        </div>
                    )}
                </div>
            </div>

            <div
                css={textStyles}
                dangerouslySetInnerHTML={{
                    __html: text,
                }}
            />
            <div css={settingsStyles}>
                <div onClick={onViewAllNews}>View All News From {name}</div>
                <ButtonSettings onClick={openNewsChatNotificationsDrawer} dim />
            </div>
        </div>
    )
})

export default PMChatNewsHeader
